import { toUtcIso, userTz } from '../utils/datetime';
import { creationIdentity, pendingCreation, acknowledgeCreation } from '../utils/createInviteIdentity';
import { useState, useContext, useEffect, useRef } from 'react';
import { AuthContext, apiClient } from '../App';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Repeat, Loader2, Home, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { normalizeApiError } from '../utils/normalizeError';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { SKILL_LEVELS } from '../constants/skillLevels';
import Footer from '../components/Footer';
import GameTimePicker from '../components/GameTimePicker';
import { useSafeBack } from '../hooks/useSafeBack';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import { DiscardChangesDialog } from '../components/DiscardChangesDialog';
import { askAfterCreateRun } from '../lib/pushPromptManager';
import {
  applySelectedLocationToForm, isValidSelectedLocation, recentLocationAddress,
  recentLocationToSelectedPlace,
} from '../utils/recentLocations';
import { buildDuplicateGameDraft } from '../utils/duplicateGame';

const MAX_PLAYERS_PRESETS = [6, 8, 10, 12, 16, 20, 24, 30, 40, 50];

const CreateGame = () => {
  const { token, user } = useContext(AuthContext);
  const generation = useRef(null);
  if (generation.current?.userId !== user?.id) {
    if (generation.current) generation.current.active = false;
    generation.current = { userId: user?.id, active: true };
  }
  const accountContext = generation.current;
  useEffect(() => { accountContext.active = true; return () => { accountContext.active = false; }; }, [accountContext]);
  const [pendingNotice, setPendingNotice] = useState(false);
  const [recoveryConfirm, setRecoveryConfirm] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const resolving = useRef(null);
  useEffect(() => { setRecoveryConfirm(false); setIsResolving(false); }, [accountContext]);
  const resolvePending = async () => {
    const pending = pendingCreation(user?.id);
    if (!pending || resolving.current === accountContext) return;
    resolving.current = accountContext;
    setIsResolving(true);
    try {
      const { data } = await apiClient.post(`/creation-invite-operations/${pending.operationId}/resolve-unsubmitted`, null,
        { headers: { 'Idempotency-Key': pending.key, 'X-Create-Invite-Actor': user.id } });
      if (!accountContext.active) return;
      if (data?.code !== 'CREATION_TERMINALLY_FENCED' || data.safe_to_clear !== true ||
          data.operation_id !== pending.operationId || data.actor_user_id !== user.id) {
        throw new Error('Uncorrelated recovery response');
      }
      if (pendingCreation(user.id)?.operationId !== pending.operationId) return;
      acknowledgeCreation(user.id, pending.operationId);
      setPendingNotice(false);
      setRecoveryConfirm(false);
      toast.success('Previous submission safely retired. Review this form, then submit when ready. Nothing was created by recovery.');
    } catch {
      if (accountContext.active) toast.error('Previous submission was not cleared. Check its results, or retry Resolve previous submission. Do not create a replacement while it remains pending.');
    } finally {
      if (resolving.current === accountContext) resolving.current = null;
      if (accountContext.active) setIsResolving(false);
    }
  };
  useEffect(() => setPendingNotice(!!pendingCreation(user?.id)), [user?.id]);
  const checkPending = async () => {
    const pending = pendingCreation(user?.id);
    if (!pending) return;
    try {
      const result = (await apiClient.get(`/creation-invite-operations/${pending.operationId}`)).data;
      if (!accountContext.active || result.operation_id !== pending.operationId || !result.game_id ||
          pendingCreation(user.id)?.operationId !== pending.operationId) return;
      acknowledgeCreation(user.id, result.operation_id);
      navigate(`/games/${result.game_id}`);
    } catch { if (accountContext.active) toast.error("The previous outcome is not established yet. Do not create another session to compensate."); }
  };
  const navigate = useNavigate();
  const location = useLocation();
  const cloneFrom = location.state?.cloneFrom || null;
  const isCloning = cloneFrom !== null;
  const { goBack } = useSafeBack('/dashboard');
  const [locationInput, setLocationInput] = useState('');
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  useEffect(() => setIsSubmitting(false), [accountContext]);
  const [recentLocations, setRecentLocations] = useState([]);
  const [recentLocationsLoading, setRecentLocationsLoading] = useState(true);
  const [recentLocationsError, setRecentLocationsError] = useState(false);
  
  // Recurring run state
  const [repeatType, setRepeatType] = useState('none'); // 'none' or 'weekly'
  const [repeatWeeks, setRepeatWeeks] = useState(2);
  
  // Run Club state (Phase 10A)
  const [myClubs, setMyClubs] = useState([]);
  const [myClubsLoaded, setMyClubsLoaded] = useState(false);
  const [selectedClubIds, setSelectedClubIds] = useState([]);
  // Max players: balanced-team capacity → even integers 2–98 (presets + custom).
  const [maxPlayersCustom, setMaxPlayersCustom] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    location_name: '',
    city: '',
    zip_code: '',
    date_time: '',
    min_players: 2,
    max_players: 10,
    skill_level_min: 'Unranked',
    skill_level_max: 'Elite',
    age_min: 13,
    age_max: 99,
    court_type: 'full_court',
    description: '',
    public_share_message: '',
    host_is_participant: true,  // checkbox: "I'm playing in this run" (default checked)
    privacy: 'public',
    is_private: false  // New: True = not in feed, only via invite/link
  });
  
  // Validation: Check if selected date_time is in the past
  const isDateTimeInPast = formData.date_time && new Date(formData.date_time) <= new Date();

  // Max players validation — balanced-team product rule: even integers 2–98.
  const maxPlayersError = (() => {
    const v = formData.max_players;
    if (v === '' || v === null || v === undefined || Number.isNaN(Number(v))) return 'Enter a number of players';
    const n = Number(v);
    if (!Number.isInteger(n)) return 'Whole numbers only';
    if (n < 2) return 'Minimum is 2 players';
    if (n > 98) return 'Maximum is 98 players';
    if (n % 2 !== 0) return 'Must be an even number (teams stay balanced)';
    return null;
  })();
  const isCustomMaxPlayers = maxPlayersCustom || !MAX_PLAYERS_PRESETS.includes(Number(formData.max_players));

  // Unsaved-changes protection: dirty once the user has entered anything.
  const isDirty = Boolean(
    formData.title || formData.description || formData.public_share_message || locationInput ||
    formData.date_time || selectedPlace || selectedClubIds.length > 0
  );
  const { promptOpen, confirmLeave, cancelLeave, guardedLeave } = useUnsavedChangesGuard({
    active: isDirty,
    shouldPrompt: isDirty,
  });

  // Fetch host's clubs for linking
  useEffect(() => {
    const fetchMyClubs = async () => {
      try {
        const res = await apiClient.get('/clubs/mine');
        setMyClubs(res.data.clubs || []);
      } catch (err) {
        // Silently fail - user may not have any clubs
      } finally {
        setMyClubsLoaded(true);
      }
    };
    fetchMyClubs();
  }, []);

  useEffect(() => {
    let active = true;
    const fetchRecentLocations = async () => {
      try {
        const response = await apiClient.get('/host/recent-locations');
        if (active) setRecentLocations(response.data.locations || []);
      } catch (error) {
        if (active) setRecentLocationsError(true);
      } finally {
        if (active) setRecentLocationsLoading(false);
      }
    };
    if (token) fetchRecentLocations();
    else setRecentLocationsLoading(false);
    return () => { active = false; };
  }, [token]);

  // Pre-select club from ?run_club_id=<id> query param (used by ClubDetail's
  // "Create Run" host shortcut). We wait until myClubs is loaded so we can
  // validate that the requested club is actually one this user hosts.
  useEffect(() => {
    if (!myClubs || myClubs.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const requested = params.get('run_club_id');
    if (requested && myClubs.some(c => c.id === requested)) {
      setSelectedClubIds([requested]);
    }
  }, [myClubs]);

  // Check for clone data from navigation state
  useEffect(() => {
    if (!cloneFrom) return;

    const draft = buildDuplicateGameDraft(cloneFrom);
    setFormData(draft.formData);
    setLocationInput(draft.locationInput);
    setSelectedPlace(draft.selectedPlace);
    setSelectedClubIds(draft.selectedClubIds);
    setMaxPlayersCustom(!MAX_PLAYERS_PRESETS.includes(Number(draft.formData.max_players)));
    setRepeatType('none');
    setRepeatWeeks(2);
    toast.info('Run details copied. Set a new date and time.');
  }, [cloneFrom]);

  // Club creation authorization remains server-authoritative. Avoid carrying a
  // source club into the new payload when it is not in the viewer's manageable
  // club list (for example, a co-host duplicating another host's club run).
  useEffect(() => {
    if (!isCloning || !myClubsLoaded) return;
    const manageableClubIds = new Set(myClubs.map((club) => club.id));
    setSelectedClubIds((current) => current.filter((clubId) => manageableClubIds.has(clubId)));
  }, [isCloning, myClubs, myClubsLoaded]);

  const toggleSelectedClub = (clubId) => {
    setSelectedClubIds((prev) => (
      prev.includes(clubId)
        ? prev.filter((id) => id !== clubId)
        : [...prev, clubId]
    ));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleTyping = (v) => {
    setLocationInput(v);
    setSelectedPlace(null);
    setFormData(prev => ({ ...prev, location_name: v, city: '', zip_code: '' }));
  };

  const handleSelect = (p) => {
    setSelectedPlace(p);
    setLocationInput(p.inputLabel);
    setFormData(prev => applySelectedLocationToForm(prev, p));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (accountContext.submitting || !accountContext.active) return;

    // Guardrails for Places
    if (!isValidSelectedLocation(selectedPlace)) {
      toast.error("Please select a valid location from the suggestions.");
      return;
    }
    
    // Validation: Cannot schedule in the past
    if (formData.date_time) {
      const selectedDateTime = new Date(formData.date_time);
      const now = new Date();
      if (selectedDateTime <= now) {
        toast.error("Game time must be in the future");
        return;
      }
    }

    accountContext.submitting = true;
    // Build the exact payload FastAPI expects
    const payload = {
      title: formData.title?.trim() || "",
      location_name: selectedPlace.location_name || formData.location_name?.trim() || "",
      address: selectedPlace.formatted_address || selectedPlace.inputLabel || "",
      city: selectedPlace.city || formData.city?.trim() || "",
      zip_code: selectedPlace.zip_code || formData.zip_code?.trim() || "",
      latitude: selectedPlace.lat || 0,
      longitude: selectedPlace.lng || 0,
      place_id: selectedPlace.place_id || null,
      date_time: toUtcIso(formData.date_time, userTz),
      tz: userTz,
      min_players: Number(formData.min_players),
      max_players: Number(formData.max_players),
      skill_level_min: formData.skill_level_min,
      skill_level_max: formData.skill_level_max,
      age_min: Number(formData.age_min),
      age_max: Number(formData.age_max),
      court_type: formData.court_type || "full_court",
      description: formData.description || null,
      public_share_message: formData.public_share_message || null,
      privacy: formData.privacy || "public",
      is_private: formData.is_private || false,
      host_is_participant: formData.host_is_participant !== false,
    };

    // Add repeat_weeks if recurring
    if (repeatType === 'weekly' && repeatWeeks >= 2) {
      payload.repeat_weeks = repeatWeeks;
    }
    
    // Add club linkage if one or more clubs are selected.
    if (selectedClubIds.length > 0) {
      payload.run_club_ids = selectedClubIds;
      payload.run_club_id = selectedClubIds[0];
    }

    // Extra safety: make sure required fields are present before POST
    const missing = [];
    ["title","location_name","address","city","zip_code","latitude","longitude","date_time","min_players","max_players","skill_level_min","skill_level_max","age_min","age_max"]
      .forEach(k => { if (payload[k] === undefined || payload[k] === "" || Number.isNaN(payload[k])) missing.push(k); });
    if (missing.length) {
      toast.error(`Please fill: ${missing.join(", ")}`);
      accountContext.submitting = false;
      return;
    }

    setIsSubmitting(true);
    let submission;
    try {
      // Guard every route before selection; a timeout cannot be bypassed by
      // deselecting clubs, switching recurrence, or reloading an edited form.
      if (pendingCreation(user?.id)) await creationIdentity(user?.id, payload);
      if (!accountContext.active) return;
      if (repeatType !== 'weekly' && selectedClubIds.length) {
        submission = await creationIdentity(user?.id, payload);
        if (!accountContext.active) return;
        setPendingNotice(true);
        const response = await apiClient.post('/games/create-and-invite', payload, { headers: { 'Idempotency-Key': submission.key, 'X-Create-Invite-Actor': user.id } });
        if (!accountContext.active) return;
        acknowledgeCreation(user.id, response.data.operation_id);
        toast.success('Session created. Invitation processing is tracked on the session page.');
        try { askAfterCreateRun(); } catch { /* existing native prompt remains best-effort */ }
        navigate(`/games/${response.data.game_id}`);
        return;
      }
      const response = await apiClient.post("/games", payload);
      
      // Handle response based on whether it's recurring or single
      if (response.data.count && response.data.count > 1) {
        toast.success(`Created ${response.data.count} recurring runs!`);
        // Show seeding toast after a short delay
        setTimeout(() => {
          toast.info('Invite a few players to get your runs started!', {
            duration: 5000,
            action: {
              label: 'Invite',
              onClick: () => navigate('/host')
            }
          });
        }, 1000);
        // Push permission ask after successful create (Capacitor-only no-op).
        try { askAfterCreateRun(); } catch { /* noop */ }
        navigate('/host'); // Go to host dashboard to see all created runs
      } else {
        toast.success("Game created!");
        // Show seeding toast after a short delay
        setTimeout(() => {
          toast.info('Invite a few players to get this run started!', {
            duration: 5000,
            action: {
              label: 'Invite',
              onClick: () => navigate('/host')
            }
          });
        }, 1000);
        // Push permission ask after successful create (Capacitor-only no-op).
        try { askAfterCreateRun(); } catch { /* noop */ }
        navigate(`/games/${response.data.id}`);
      }
    } catch (err) {
      const rejection = err?.response?.data?.detail;
      const rejected = submission && rejection?.code === 'CREATION_REJECTED'
        && rejection.operation_id === submission.operationId;
      if (accountContext.active && rejected) {
        acknowledgeCreation(user.id, submission.operationId);
        setPendingNotice(!!pendingCreation(user.id));
      }
      if (accountContext.active) toast.error(rejected ? rejection.message : normalizeApiError(err));
    } finally {
      accountContext.submitting = false;
      if (accountContext.active) setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <div className="flex items-center gap-2 mb-6">
          <Button
            variant="ghost"
            onClick={() => guardedLeave(goBack)}
            className="h-10 px-3 text-[14px] font-medium text-[#52525B] hover:text-[#111111] hover:bg-[#FFFFFF] rounded-[12px] gap-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => guardedLeave(() => navigate('/dashboard'))}
            className="h-8 w-8 rounded-lg"
            data-testid="nav-home-btn"
            aria-label="Dashboard"
          >
            <Home className="w-4 h-4 text-zinc-400" />
          </Button>
        </div>

        <section className="bg-[#FFFFFF] rounded-[16px] border border-[#E4E4E7] p-6 sm:p-8">
          <h1 className="text-[24px] sm:text-[32px] font-bold text-[#111111] tracking-[-0.02em] mb-2">
            {isCloning ? 'Duplicate Run' : 'Create a New Run'}
          </h1>
          {isCloning && (
            <div className="mb-6 space-y-2 rounded-xl border border-orange-200 bg-orange-50 p-4">
              <p className="text-sm font-medium text-zinc-800">
                Duplicating "{cloneFrom.title}". Set a new date and adjust details as needed.
              </p>
              <p className="text-sm text-zinc-600" data-testid="duplicate-exclusions-note">
                Players, invitations, waitlists, confirmations, and messages will not be copied.
              </p>
            </div>
          )}

          {pendingNotice && <div role="status" className="mb-4 rounded border border-orange-200 p-3">
            <p>A previous Create &amp; Invite submission may still be processing.</p>
            <button type="button" className="min-h-[44px] underline" onClick={checkPending}>Check previous submission</button>
            <button type="button" className="min-h-[44px] underline ml-3" disabled={isResolving} onClick={() => setRecoveryConfirm(true)}>Resolve previous submission</button>
            {recoveryConfirm && <div>
              <p>If the original form values are lost, the server can permanently retire the previous identity only if no session was accepted or related work exists. This does not create a run or send invitations. Your current form will stay unchanged.</p>
              <button type="button" data-testid="retire-pending" className="min-h-[44px] underline" disabled={isResolving} onClick={resolvePending}>{isResolving ? 'Checking and resolving…' : 'Retire previous submission without creating'}</button>
            </div>}
          </div>}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="title" className="text-[14px] font-medium text-[#111111]">Run Title *</Label>
              <Input
                id="title"
                name="title"
                data-testid="title-input"
                placeholder="e.g., Saturday Morning Run"
                value={formData.title}
                onChange={handleInputChange}
                required
                className="rounded-[12px] mt-1"
              />
            </div>

            {/* Run Club Selector (Phase 2A) - Only show if host has clubs */}
            {myClubs.length > 0 && (
              <div>
                <Label htmlFor="run_club" className="text-[14px] font-medium text-[#111111]">
                  {repeatType === 'weekly' ? 'Post to Clubs' : 'Invite clubs'} <span className="text-gray-400 font-normal">(optional)</span>
                </Label>
                <div
                  id="run_club"
                  className="mt-2 space-y-2 rounded-[12px] border border-zinc-200 bg-white p-2"
                  data-testid="club-multiselect"
                >
                  {myClubs.map((club) => {
                    const selected = selectedClubIds.includes(club.id);
                    return (
                      <button
                        key={club.id}
                        type="button"
                        onClick={() => toggleSelectedClub(club.id)}
                        className={`w-full flex items-center justify-between gap-3 rounded-[10px] px-3 py-2 text-left transition-colors ${
                          selected
                            ? 'bg-orange-50 text-zinc-950 ring-1 ring-orange-200'
                            : 'bg-white text-zinc-700 hover:bg-zinc-50'
                        }`}
                        data-testid={`club-option-${club.id}`}
                        aria-pressed={selected}
                      >
                        <span className="min-w-0 truncate text-sm font-medium">{club.name}</span>
                        <span
                          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[11px] font-bold ${
                            selected
                              ? 'border-orange-500 bg-orange-500 text-white'
                              : 'border-zinc-300 text-transparent'
                          }`}
                          aria-hidden="true"
                        >
                          ✓
                        </span>
                      </button>
                    );
                  })}
                </div>
                {selectedClubIds.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2" data-testid="selected-club-chips">
                    {selectedClubIds.map((clubId) => {
                      const club = myClubs.find((item) => item.id === clubId);
                      if (!club) return null;
                      return (
                        <button
                          key={clubId}
                          type="button"
                          onClick={() => toggleSelectedClub(clubId)}
                          className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-200"
                          data-testid={`selected-club-chip-${clubId}`}
                        >
                          {club.name}
                          <span className="text-zinc-500" aria-hidden="true">×</span>
                        </button>
                      );
                    })}
                  </div>
                )}
                <p className="text-[13px] text-zinc-600 mt-1">
                  {repeatType === 'weekly' ? 'Club runs appear on selected club pages. Public runs may also appear in the main feed. Creating recurring runs does not send member invitations. You can invite members from each run after creation.' : 'Create & Invite creates this session, posts it to the selected clubs, and processes invitations for eligible members. Members already invited or on the roster are skipped. Notification availability depends on existing settings and supported channels.'}
                </p>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="min_players">Min Players *</Label>
                <Select
                  value={formData.min_players?.toString()}
                  onValueChange={(value) => setFormData({ ...formData, min_players: parseInt(value) })}
                >
                  <SelectTrigger data-testid="min-players-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2,3,4,5,6,7,8,9,10].map(n => (
                      <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="max_players">Max Players *</Label>
                <Select
                  value={isCustomMaxPlayers ? 'custom' : String(formData.max_players)}
                  onValueChange={(value) => {
                    if (value === 'custom') {
                      setMaxPlayersCustom(true);
                    } else {
                      setMaxPlayersCustom(false);
                      setFormData({ ...formData, max_players: parseInt(value) });
                    }
                  }}
                >
                  <SelectTrigger data-testid="max-players-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MAX_PLAYERS_PRESETS.map(n => (
                      <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                    ))}
                    <SelectItem value="custom">Custom…</SelectItem>
                  </SelectContent>
                </Select>
                {isCustomMaxPlayers && (
                  <input
                    type="number"
                    min="2"
                    max="98"
                    step="2"
                    value={formData.max_players === '' || formData.max_players === undefined ? '' : formData.max_players}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setFormData({ ...formData, max_players: raw === '' ? '' : parseInt(raw, 10) });
                    }}
                    placeholder="Even number 2–98"
                    className="mt-2 w-full px-3 py-2 border border-zinc-300 rounded-md focus:ring-2 focus:ring-orange-500"
                    data-testid="max-players-custom-input"
                  />
                )}
                {maxPlayersError && (
                  <p className="mt-1 text-xs text-red-600" data-testid="max-players-error">{maxPlayersError}</p>
                )}
              </div>
            </div>

            <div>
              <Label>Location *</Label>
              <AddressAutocomplete
                value={locationInput}
                onTyping={handleTyping}
                onSelect={handleSelect}
                placeholder="Search for court or address..."
              />
              <div className="mt-3" aria-live="polite">
                <div className="text-sm font-medium text-zinc-700">Recent locations</div>
                {recentLocationsLoading && <div className="mt-2 flex items-center gap-2 text-sm text-zinc-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading recent locations…</div>}
                {!recentLocationsLoading && recentLocationsError && <p className="mt-2 text-sm text-zinc-500">Recent locations are unavailable. You can still search for a location.</p>}
                {!recentLocationsLoading && !recentLocationsError && recentLocations.length === 0 && <p className="mt-2 text-sm text-zinc-500">No recent locations yet.</p>}
                {!recentLocationsLoading && !recentLocationsError && recentLocations.length > 0 && (
                  <div className="mt-2 grid gap-2" data-testid="recent-locations-list">
                    {recentLocations.map((recent, index) => {
                      const address = recentLocationAddress(recent);
                      const label = recent.location_name || address || 'Recent location';
                      return (
                        <button
                          key={`${recent.place_id || `${recent.latitude}-${recent.longitude}` || address}-${index}`}
                          type="button"
                          onClick={() => handleSelect(recentLocationToSelectedPlace(recent))}
                          aria-label={`Use ${label}${address && address !== label ? `, ${address}` : ''}`}
                          className="flex min-h-12 w-full items-start gap-3 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-left hover:bg-orange-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
                        >
                          <MapPin className="mt-0.5 h-4 w-4 flex-none text-orange-500" aria-hidden="true" />
                          <span className="min-w-0">
                            <span className="block break-words text-sm font-medium text-zinc-900">{label}</span>
                            {address && address !== label && <span className="block break-words text-xs text-zinc-500">{address}</span>}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <GameTimePicker
              value={formData.date_time}
              onChange={(val) => setFormData(prev => ({ ...prev, date_time: val }))}
              label="Date & Time *"
              required
              helpText="Runs can be scheduled up to 60 days in advance"
            />
            {isDateTimeInPast && (
              <p className="text-sm text-red-600 -mt-2" data-testid="past-date-error">
                Game time must be in the future
              </p>
            )}

            {/* Recurring Run Option */}
            <div className="p-4 bg-zinc-50 rounded-xl border border-zinc-200">
              <div className="flex items-center gap-2 mb-3">
                <Repeat className="w-4 h-4 text-zinc-600" />
                <Label className="text-[14px] font-medium text-[#111111]">Repeat</Label>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Select
                  value={repeatType}
                  onValueChange={setRepeatType}
                >
                  <SelectTrigger data-testid="repeat-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (Single Run)</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                  </SelectContent>
                </Select>
                
                {repeatType === 'weekly' && (
                  <Select
                    value={repeatWeeks.toString()}
                    onValueChange={(v) => setRepeatWeeks(parseInt(v))}
                  >
                    <SelectTrigger data-testid="repeat-weeks-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2,3,4,5,6].map(n => (
                        <SelectItem key={n} value={n.toString()}>{n} weeks</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              {repeatType === 'weekly' && (
                <p className="text-[13px] text-zinc-600 mt-2">
                  This will create {repeatWeeks} independent runs, each 7 days apart. Each run can be edited or cancelled separately.
                </p>
              )}
            </div>

            <div>
              <Label>Skill Level Range *</Label>
              <div className="grid md:grid-cols-2 gap-4">
                <Select
                  value={formData.skill_level_min}
                  onValueChange={(value) => setFormData({ ...formData, skill_level_min: value })}
                >
                  <SelectTrigger data-testid="skill-min-select">
                    <SelectValue placeholder="Min" />
                  </SelectTrigger>
                  <SelectContent>
                    {SKILL_LEVELS.map((opt) => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={formData.skill_level_max}
                  onValueChange={(value) => setFormData({ ...formData, skill_level_max: value })}
                >
                  <SelectTrigger data-testid="skill-max-select">
                    <SelectValue placeholder="Max" />
                  </SelectTrigger>
                  <SelectContent>
                    {SKILL_LEVELS.map((opt) => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="age_min">Min Age *</Label>
                <Input
                  id="age_min"
                  name="age_min"
                  type="number"
                  data-testid="age-min-input"
                  value={formData.age_min}
                  onChange={handleInputChange}
                  min="13"
                  max="100"
                  required
                />
              </div>
              <div>
                <Label htmlFor="age_max">Max Age *</Label>
                <Input
                  id="age_max"
                  name="age_max"
                  type="number"
                  data-testid="age-max-input"
                  value={formData.age_max}
                  onChange={handleInputChange}
                  min="13"
                  max="100"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="court_type">Run Type *</Label>
              <Select
                value={formData.court_type}
                onValueChange={(value) => setFormData({ ...formData, court_type: value })}
              >
                <SelectTrigger data-testid="court-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full_court">Full Court</SelectItem>
                  <SelectItem value="half_court">Half Court</SelectItem>
                  <SelectItem value="training">Training</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                name="description"
                data-testid="description-input"
                placeholder="Add any additional details about the run..."
                value={formData.description}
                onChange={handleInputChange}
                rows={4}
              />
            </div>

            <div>
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="public_share_message">Public share message</Label>
                <span className="text-xs text-zinc-500">
                  {(formData.public_share_message || '').length}/180
                </span>
              </div>
              <Textarea
                id="public_share_message"
                name="public_share_message"
                data-testid="public-share-message-input"
                placeholder="Friday night run. Need 8 more. Grab a spot."
                value={formData.public_share_message}
                onChange={handleInputChange}
                maxLength={180}
                rows={3}
              />
              <p className="text-[13px] text-zinc-600 mt-1">
                Visible on shared links and Instagram cards. Keep it public.
              </p>
            </div>

            <div>
              <Label>Your role</Label>
              <label className="flex items-center gap-3 cursor-pointer p-3 mt-2 rounded-lg border border-zinc-200 hover:bg-zinc-50 transition-colors">
                <input
                  type="checkbox"
                  name="host_is_participant"
                  checked={formData.host_is_participant !== false}
                  onChange={(e) => setFormData(prev => ({ ...prev, host_is_participant: e.target.checked }))}
                  className="w-5 h-5 rounded border-zinc-300 text-orange-500 focus:ring-orange-500"
                  data-testid="host-is-participant-checkbox"
                />
                <div>
                  <span className="font-medium text-zinc-900">I'm playing in this run</span>
                  <p className="text-[13px] text-zinc-600">Uncheck this if you're organizing, coaching, or hosting but not participating.</p>
                </div>
              </label>
            </div>

            <div>
              <Label>Run Visibility *</Label>
              <div className="flex flex-col gap-3 mt-2">
                {/* Private Game Toggle */}
                <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-zinc-200 hover:bg-zinc-50 transition-colors">
                  <input
                    type="checkbox"
                    name="is_private"
                    checked={formData.is_private}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_private: e.target.checked }))}
                    className="w-5 h-5 rounded border-zinc-300 text-orange-500 focus:ring-orange-500"
                    data-testid="is-private-checkbox"
                  />
                  <div>
                    <span className="font-medium text-zinc-900">Private Game</span>
                    <p className="text-[13px] text-zinc-600">Only accessible via invite link. Won't appear in the public feed.</p>
                  </div>
                </label>
                
                {/* Legacy privacy options - only show if not private */}
                {!formData.is_private && (
                  <div className="flex flex-col sm:flex-row gap-4 mt-1">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="privacy"
                        value="public"
                        checked={formData.privacy === 'public'}
                        onChange={handleInputChange}
                        className="w-4 h-4"
                      />
                      <span className="text-[14px] text-[#52525B]">Public (Anyone can discover)</span>
                    </label>
                  </div>
                )}
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 bg-[#F97316] hover:bg-[#EA580C] text-white rounded-[12px] text-[15px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed" 
              data-testid="create-submit-btn"
              disabled={isSubmitting || isDateTimeInPast || !!maxPlayersError}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                repeatType === 'weekly' ? `Create ${repeatWeeks} Weekly Runs` : selectedClubIds.length ? 'Create & Invite' : (isCloning ? 'Create Duplicate' : 'Create Run')
              )}
            </Button>
          </form>
        </section>
      </div>
      <Footer variant="slim" />
      <DiscardChangesDialog
        open={promptOpen}
        onKeepEditing={cancelLeave}
        onDiscard={confirmLeave}
      />
    </div>
  );
};

export default CreateGame;
